import { useState } from 'react';
import { Button, Panel, PanelHeader } from '@/components/ui';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { newIdempotencyKey } from '@/api/http';

/**
 * Approving the exact collector version the walk executor may run.
 *
 * A walk only runs a collector build that has been approved, and approving one
 * existed as SQL alone. That put a step of the operating loop outside the
 * product: nobody could tell from here which build was blessed, and shipping a
 * new collector meant a database session.
 *
 * Both fields are exact by design. There is no "latest": approving a version
 * you did not name is how an unreviewed build reaches a customer's data.
 */
export function CollectorApproval() {
  const [slug, setSlug] = useState('');
  const [version, setVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const field =
    'mono min-w-0 flex-1 rounded border border-line bg-panel-2 px-2.5 py-1.5 text-small text-ink placeholder:text-ink-4 focus:border-acc focus:outline-none';

  const submit = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = (await backend.approveCollectorVersion(slug.trim(), version.trim(), newIdempotencyKey())) as {
        data?: { created?: boolean };
        created?: boolean;
      };
      const created = r.data?.created ?? r.created;
      setNote(
        created === false
          ? 'That version was already approved, so nothing changed.'
          : `Approved. The walk executor may now run ${slug.trim()} ${version.trim()}.`,
      );
    } catch (e) {
      setNote(explainLine(e, 'approve a collector build'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel className="mb-3">
      <PanelHeader
        title="Approve a collector build"
        sub="a walk runs only the collector versions approved here"
      />
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="collector slug" aria-label="Collector slug" className={field} />
        <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="exact version" aria-label="Collector version" className={field} />
        <Button disabled={busy || !slug.trim() || !version.trim()} loading={busy} onClick={() => void submit()}>
          {busy ? 'Approving…' : 'Approve this version'}
        </Button>
        <span className="w-full text-caption text-ink-4">
          {note ?? 'Both are exact. There is no latest, because approving a build nobody named is how an unreviewed collector reaches customer data.'}
        </span>
      </div>
    </Panel>
  );
}
